import {
  MCP_RESOURCES,
  normalizeMcpResourceScopes,
  scopesBelongToMcpResource,
  type McpResource,
} from "@arti/schemas/mcpResources";
import { type FC, useMemo, useState } from "react";

import type {
  DashboardMcpGrant,
  DashboardMcpToken,
  DashboardMcpTokenCreateResponse,
} from "../../dashboard-client";

import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import { useMcpTokens } from "../../hooks/useMcpTokens";
import { Alert } from "../common/Alert";
import { Button } from "../common/Button";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { CopyButton } from "../common/CopyButton";
import { DataTable } from "../common/DataTable";
import { EmptyState } from "../common/EmptyState";
import { FormInput } from "../common/forms/FormInput";
import { LoadingState } from "../common/LoadingState";
import { PanelSection } from "../common/PanelSection";
import { McpEndpointTile, McpSummaryCard } from "./McpSummaryTiles";
import { McpTokenScopePicker } from "./McpTokenScopePicker";

function formatTokenDate(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "Never";
}

function formatTokenId(tokenId: string): string {
  return tokenId.length <= 14
    ? tokenId
    : `${tokenId.slice(0, 8)}...${tokenId.slice(-4)}`;
}

function formatRelativeDate(value: string | null): string {
  if (!value) {
    return "Never";
  }

  const deltaMs = Date.now() - new Date(value).getTime();
  const deltaMinutes = Math.max(1, Math.round(deltaMs / 60_000));
  if (deltaMinutes < 60) {
    return `${deltaMinutes}m ago`;
  }

  const deltaHours = Math.round(deltaMinutes / 60);
  if (deltaHours < 48) {
    return `${deltaHours}h ago`;
  }

  return `${Math.round(deltaHours / 24)}d ago`;
}

function getMcpEndpoint(resource: McpResource): string {
  if (typeof window === "undefined") {
    return MCP_RESOURCES[resource].path;
  }
  return `${window.location.origin}${MCP_RESOURCES[resource].path}`;
}

function getUrlHost(value: string): string {
  try {
    return new URL(value).host;
  } catch {
    return value;
  }
}

function getLatestUsage(
  items: readonly { last_used_at: string | null }[],
): string | null {
  return items.reduce<string | null>((latest, item) => {
    if (!item.last_used_at) {
      return latest;
    }
    if (!latest) {
      return item.last_used_at;
    }
    return new Date(latest).getTime() >= new Date(item.last_used_at).getTime()
      ? latest
      : item.last_used_at;
  }, null);
}

function getScopeCount(
  items: readonly { scopes: readonly string[] }[],
): number {
  return new Set(items.flatMap((item) => item.scopes)).size;
}

function ScopeList({ scopes }: { scopes: string[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {scopes.map((scope) => (
        <span
          key={scope}
          className="border border-slate-300 bg-slate-100 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
        >
          {scope}
        </span>
      ))}
    </div>
  );
}

function LatestTokenCard({
  token,
}: {
  token: DashboardMcpTokenCreateResponse;
}) {
  return (
    <div className="space-y-3">
      <Alert variant="warning" title="Shown once">
        This token value is only returned at creation time. Copy it now.
      </Alert>
      <div className="border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-slate-50 p-4 dark:border-emerald-500/30 dark:from-emerald-500/10 dark:via-slate-950/40 dark:to-slate-900/80">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.24em] text-emerald-800 dark:text-emerald-200">
              {token.label}
            </p>
            <ScopeList scopes={token.scopes} />
            <p className="text-xs text-slate-700 dark:text-slate-300">
              Expires {formatTokenDate(token.expires_at)}
            </p>
          </div>
          <CopyButton text={token.token} />
        </div>
        <div className="mt-4 overflow-x-auto border border-slate-200 bg-white/90 p-3 font-mono text-[11px] text-slate-800 dark:border-slate-700 dark:bg-slate-950/50 dark:text-slate-100">
          {token.token}
        </div>
      </div>
    </div>
  );
}

function EndpointPanel({
  grantCount,
  endpoint,
  latestUsage,
  scopeCount,
  tokenCount,
}: {
  grantCount: number;
  endpoint: string;
  latestUsage: string | null;
  scopeCount: number;
  tokenCount: number;
}) {
  return (
    <PanelSection title="Global MCP Endpoint">
      <div className="space-y-4">
        <Alert variant="info" title="Global routing">
          Tokens generated here are user-bound and organization-bound. The
          bearer token decides which organization-scoped MCP data the global
          endpoint can expose.
        </Alert>
        <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))]">
          <McpEndpointTile endpoint={endpoint} />
          <McpSummaryCard
            eyebrow="Active Tokens"
            tone="emerald"
            value={String(tokenCount)}
          />
          <McpSummaryCard
            eyebrow="Connected Apps"
            tone="slate"
            value={String(grantCount)}
          />
          <McpSummaryCard
            eyebrow="Last Activity"
            tone="slate"
            value={formatRelativeDate(latestUsage)}
          />
          <McpSummaryCard
            eyebrow="Scopes Seen"
            tone="emerald"
            value={String(scopeCount)}
          />
        </div>
      </div>
    </PanelSection>
  );
}

function GenerateTokenPanel({
  creating,
  label,
  latestToken,
  onCreate,
  onLabelChange,
  onScopesChange,
  selectedScopes,
}: {
  creating: boolean;
  label: string;
  latestToken: DashboardMcpTokenCreateResponse | null;
  onCreate: () => void;
  onLabelChange: (value: string) => void;
  onScopesChange: (scopes: string[]) => void;
  selectedScopes: string[];
}) {
  return (
    <PanelSection title="Generate Token">
      <div className="space-y-4">
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
          <FormInput
            aria-label="Label"
            label="Label"
            placeholder="CLI token"
            value={label}
            onChange={(event) => onLabelChange(event.target.value)}
            helperText="Optional. Use labels that identify the calling client or workflow."
          />
          <div className="lg:pb-[3px]">
            <Button
              variant="primary"
              onClick={onCreate}
              loading={creating}
              loadingText="Generating..."
            >
              Generate Token
            </Button>
          </div>
        </div>

        <McpTokenScopePicker
          onChange={onScopesChange}
          selectedScopes={selectedScopes}
        />

        {latestToken && <LatestTokenCard token={latestToken} />}
      </div>
    </PanelSection>
  );
}

function TokensTable({
  revokingTokenId,
  setRevokeTarget,
  tokens,
}: {
  revokingTokenId: string | null;
  setRevokeTarget: (token: DashboardMcpToken) => void;
  tokens: DashboardMcpToken[];
}) {
  return (
    <DataTable responsive>
      <DataTable.Header>
        <tr>
          <DataTable.HeaderCell>Label</DataTable.HeaderCell>
          <DataTable.HeaderCell className="hidden md:table-cell">
            Token ID
          </DataTable.HeaderCell>
          <DataTable.HeaderCell className="hidden lg:table-cell">
            Scopes
          </DataTable.HeaderCell>
          <DataTable.HeaderCell className="hidden sm:table-cell">
            Last Used
          </DataTable.HeaderCell>
          <DataTable.HeaderCell className="hidden xl:table-cell">
            Created
          </DataTable.HeaderCell>
          <DataTable.HeaderCell className="hidden lg:table-cell">
            Expires
          </DataTable.HeaderCell>
          <DataTable.HeaderCell>Actions</DataTable.HeaderCell>
        </tr>
      </DataTable.Header>
      <DataTable.Body>
        {tokens.map((token) => (
          <DataTable.Row key={token.token_id}>
            <DataTable.Cell className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
              <div className="space-y-1">
                <div>{token.label}</div>
                <div className="font-mono text-[10px] font-normal text-slate-500 md:hidden dark:text-slate-400">
                  {formatTokenId(token.token_id)}
                </div>
                <div className="text-[10px] font-normal text-slate-500 sm:hidden dark:text-slate-400">
                  {formatRelativeDate(token.last_used_at)}
                </div>
              </div>
            </DataTable.Cell>
            <DataTable.Cell className="hidden font-mono text-[11px] md:table-cell">
              <span title={token.token_id}>
                {formatTokenId(token.token_id)}
              </span>
            </DataTable.Cell>
            <DataTable.Cell className="hidden lg:table-cell">
              <ScopeList scopes={token.scopes} />
            </DataTable.Cell>
            <DataTable.Cell className="hidden sm:table-cell">
              <div className="space-y-1">
                <div className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
                  {formatRelativeDate(token.last_used_at)}
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400">
                  {formatTokenDate(token.last_used_at)}
                </div>
              </div>
            </DataTable.Cell>
            <DataTable.Cell className="hidden xl:table-cell">
              {formatTokenDate(token.created_at)}
            </DataTable.Cell>
            <DataTable.Cell className="hidden lg:table-cell">
              {formatTokenDate(token.expires_at)}
            </DataTable.Cell>
            <DataTable.Cell>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setRevokeTarget(token)}
                loading={revokingTokenId === token.token_id}
                loadingText="Revoking..."
              >
                Revoke
              </Button>
            </DataTable.Cell>
          </DataTable.Row>
        ))}
      </DataTable.Body>
    </DataTable>
  );
}

function ConnectedAppsTable({
  grants,
  onRevoke,
  revokingGrantId,
}: {
  grants: DashboardMcpGrant[];
  onRevoke: (grant: DashboardMcpGrant) => void;
  revokingGrantId: string | null;
}) {
  return (
    <DataTable responsive>
      <DataTable.Header>
        <tr>
          <DataTable.HeaderCell>Connector</DataTable.HeaderCell>
          <DataTable.HeaderCell className="hidden md:table-cell">
            Route
          </DataTable.HeaderCell>
          <DataTable.HeaderCell className="hidden lg:table-cell">
            Scopes
          </DataTable.HeaderCell>
          <DataTable.HeaderCell className="hidden sm:table-cell">
            Last Used
          </DataTable.HeaderCell>
          <DataTable.HeaderCell className="hidden lg:table-cell">
            Granted
          </DataTable.HeaderCell>
          <DataTable.HeaderCell>Actions</DataTable.HeaderCell>
        </tr>
      </DataTable.Header>
      <DataTable.Body>
        {grants.map((grant) => (
          <DataTable.Row key={grant.grant_id}>
            <DataTable.Cell className="min-w-[180px] sm:min-w-[220px]">
              <div className="space-y-1">
                <div className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
                  {grant.client_name}
                </div>
                <div className="font-mono text-[10px] text-slate-500 md:text-[11px] dark:text-slate-400">
                  {grant.oauth_client_id}
                </div>
                <div className="font-mono text-[10px] text-slate-500 md:hidden dark:text-slate-400">
                  {getUrlHost(grant.redirect_uri)}
                </div>
                <div className="text-[10px] text-slate-500 sm:hidden dark:text-slate-400">
                  {formatRelativeDate(grant.last_used_at)}
                </div>
              </div>
            </DataTable.Cell>
            <DataTable.Cell className="hidden min-w-[220px] md:table-cell">
              <div className="space-y-1">
                <div className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
                  {getUrlHost(grant.redirect_uri)}
                </div>
                <div className="font-mono text-[11px] text-slate-500 dark:text-slate-400">
                  {grant.resource}
                </div>
              </div>
            </DataTable.Cell>
            <DataTable.Cell className="hidden lg:table-cell">
              <ScopeList scopes={grant.scopes} />
            </DataTable.Cell>
            <DataTable.Cell className="hidden sm:table-cell">
              <div className="space-y-1">
                <div className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
                  {formatRelativeDate(grant.last_used_at)}
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400">
                  {formatTokenDate(grant.last_used_at)}
                </div>
              </div>
            </DataTable.Cell>
            <DataTable.Cell className="hidden lg:table-cell">
              {formatTokenDate(grant.created_at)}
            </DataTable.Cell>
            <DataTable.Cell>
              <Button
                variant="danger"
                size="sm"
                onClick={() => onRevoke(grant)}
                loading={revokingGrantId === grant.grant_id}
                loadingText="Revoking..."
              >
                Revoke
              </Button>
            </DataTable.Cell>
          </DataTable.Row>
        ))}
      </DataTable.Body>
    </DataTable>
  );
}

function ConnectedAppsPanel({
  grants,
  loading,
  onRevoke,
  revokingGrantId,
}: {
  grants: DashboardMcpGrant[];
  loading: boolean;
  onRevoke: (grant: DashboardMcpGrant) => void;
  revokingGrantId: string | null;
}) {
  if (loading) {
    return (
      <PanelSection title="Connected Apps" badge={<span>{grants.length}</span>}>
        <LoadingState message="Loading connector grants..." />
      </PanelSection>
    );
  }

  if (grants.length === 0) {
    return (
      <PanelSection title="Connected Apps" badge={<span>0</span>}>
        <div className="space-y-4">
          <Alert variant="info" title="OAuth connector grants">
            Claude and other OAuth clients will appear here after a user grants
            access through the MCP connector flow.
          </Alert>
          <EmptyState
            title="No connected apps yet"
            description="Authorize Claude or another OAuth client to create a revocable connector grant."
          />
        </div>
      </PanelSection>
    );
  }

  return (
    <PanelSection title="Connected Apps" badge={<span>{grants.length}</span>}>
      <div className="space-y-4">
        <Alert variant="warning" title="Immediate revoke">
          Revoking a connector invalidates both its refresh tokens and the
          active MCP bearer access tokens tied to this organization.
        </Alert>
        <ConnectedAppsTable
          grants={grants}
          onRevoke={onRevoke}
          revokingGrantId={revokingGrantId}
        />
      </div>
    </PanelSection>
  );
}

function TokenInventoryPanel({
  loading,
  resource,
  revokingTokenId,
  setRevokeTarget,
  tokens,
}: {
  loading: boolean;
  resource: McpResource;
  revokingTokenId: string | null;
  setRevokeTarget: (token: DashboardMcpToken) => void;
  tokens: DashboardMcpToken[];
}) {
  if (loading) {
    return (
      <PanelSection title="Active Tokens" badge={<span>{tokens.length}</span>}>
        <LoadingState message="Loading MCP access tokens..." />
      </PanelSection>
    );
  }

  if (tokens.length === 0) {
    return (
      <PanelSection title="Active Tokens" badge={<span>0</span>}>
        <EmptyState
          title="No MCP tokens yet"
          description={`Generate a token to connect a client to ${MCP_RESOURCES[resource].name}.`}
        />
      </PanelSection>
    );
  }

  return (
    <PanelSection title="Active Tokens" badge={<span>{tokens.length}</span>}>
      <TokensTable
        revokingTokenId={revokingTokenId}
        setRevokeTarget={setRevokeTarget}
        tokens={tokens}
      />
    </PanelSection>
  );
}

export const McpTokensPanel: FC = () => {
  const auth = useDashboardAuth();
  return (
    <McpAccessPanel
      key={auth.organizationId ?? "none"}
      organizationId={auth.organizationId}
      resource="arti"
    />
  );
};

const McpAccessPanel: FC<{
  organizationId: string | null;
  resource: McpResource;
}> = ({ organizationId, resource }) => {
  const {
    createToken,
    error: loadError,
    grants,
    loading,
    revokeGrant,
    revokeToken,
    tokens,
  } = useMcpTokens(organizationId, resource);
  const [label, setLabel] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [latestToken, setLatestToken] =
    useState<DashboardMcpTokenCreateResponse | null>(null);
  const [selectedScopes, setSelectedScopes] = useState<string[]>(
    normalizeMcpResourceScopes(resource),
  );
  const [revokeTarget, setRevokeTarget] = useState<DashboardMcpToken | null>(
    null,
  );
  const [grantRevokeTarget, setGrantRevokeTarget] =
    useState<DashboardMcpGrant | null>(null);
  const [revokingGrantId, setRevokingGrantId] = useState<string | null>(null);
  const [revokingTokenId, setRevokingTokenId] = useState<string | null>(null);

  const endpoint = useMemo(() => getMcpEndpoint(resource), [resource]);
  const latestUsage = useMemo(
    () => getLatestUsage([...tokens, ...grants]),
    [grants, tokens],
  );
  const scopeCount = useMemo(
    () => getScopeCount([...tokens, ...grants]),
    [grants, tokens],
  );

  if (!organizationId) {
    return (
      <EmptyState
        title="No organization selected"
        description="Select an organization before generating MCP access tokens."
      />
    );
  }

  const handleCreateToken = async () => {
    setActionError(null);
    setCreating(true);
    try {
      const token = await createToken(label, selectedScopes);
      setLatestToken(token);
      setLabel("");
    } catch (caughtError) {
      setActionError(
        caughtError instanceof Error
          ? caughtError.message
          : "Failed to create MCP token",
      );
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeToken = async () => {
    if (!revokeTarget) {
      return;
    }

    setActionError(null);
    setRevokingTokenId(revokeTarget.token_id);
    try {
      await revokeToken(revokeTarget.token_id);
      if (latestToken?.token_id === revokeTarget.token_id) {
        setLatestToken(null);
      }
      setRevokeTarget(null);
    } catch (caughtError) {
      setActionError(
        caughtError instanceof Error
          ? caughtError.message
          : "Failed to revoke MCP token",
      );
    } finally {
      setRevokingTokenId(null);
    }
  };

  const handleRevokeGrant = async () => {
    if (!grantRevokeTarget) {
      return;
    }

    setActionError(null);
    setRevokingGrantId(grantRevokeTarget.grant_id);
    try {
      await revokeGrant(grantRevokeTarget.grant_id);
      setGrantRevokeTarget(null);
    } catch (caughtError) {
      setActionError(
        caughtError instanceof Error
          ? caughtError.message
          : "Failed to revoke connector grant",
      );
    } finally {
      setRevokingGrantId(null);
    }
  };

  return (
    <div className="space-y-4">
      <EndpointPanel
        grantCount={grants.length}
        endpoint={endpoint}
        latestUsage={latestUsage}
        scopeCount={scopeCount}
        tokenCount={tokens.length}
      />
      <GenerateTokenPanel
        creating={creating}
        label={label}
        latestToken={latestToken}
        onCreate={() => void handleCreateToken()}
        onLabelChange={setLabel}
        onScopesChange={setSelectedScopes}
        selectedScopes={selectedScopes}
      />

      {actionError && <Alert variant="error">{actionError}</Alert>}
      {loadError && <Alert variant="error">{loadError}</Alert>}

      <ConnectedAppsPanel
        grants={grants}
        loading={loading}
        onRevoke={setGrantRevokeTarget}
        revokingGrantId={revokingGrantId}
      />

      {tokens.some(
        (token) => !scopesBelongToMcpResource(resource, token.scopes),
      ) && (
        <Alert variant="warning" title="Replace legacy combined tokens">
          Tokens that combine ARTI and Skill Mesh permissions no longer
          authenticate. Revoke them and create separate tokens on each MCP’s
          settings page.
        </Alert>
      )}

      <TokenInventoryPanel
        loading={loading}
        resource={resource}
        revokingTokenId={revokingTokenId}
        setRevokeTarget={setRevokeTarget}
        tokens={tokens}
      />

      <ConfirmationDialog
        isOpen={revokeTarget != null}
        title="Revoke MCP Token"
        body={
          <div className="space-y-2 text-sm">
            <p>
              Revoke <span className="font-mono">{revokeTarget?.label}</span>?
            </p>
            <p>
              This revokes this token’s access to {MCP_RESOURCES[resource].name}
              .
            </p>
          </div>
        }
        confirmLabel="Revoke Token"
        onConfirm={() => void handleRevokeToken()}
        onClose={() => {
          if (!revokingTokenId) {
            setRevokeTarget(null);
          }
        }}
        isProcessing={revokingTokenId != null}
      />

      <ConfirmationDialog
        isOpen={grantRevokeTarget != null}
        title="Revoke Connector Access"
        body={
          <div className="space-y-2 text-sm">
            <p>
              Revoke{" "}
              <span className="font-mono">
                {grantRevokeTarget?.client_name}
              </span>
              ?
            </p>
            <p>
              This connected app will lose its current{" "}
              {MCP_RESOURCES[resource].name} access immediately and will need to
              complete OAuth again.
            </p>
          </div>
        }
        confirmLabel="Revoke Access"
        onConfirm={() => void handleRevokeGrant()}
        onClose={() => {
          if (!revokingGrantId) {
            setGrantRevokeTarget(null);
          }
        }}
        isProcessing={revokingGrantId != null}
      />
    </div>
  );
};
